import { Injectable, inject } from '@angular/core'
import { AsyncSubject, Observable, ReplaySubject } from 'rxjs'
import { LoginProvider } from './entities/login-provider'
import { SocialUser } from './entities/social-user'
import { GoogleLoginProvider } from './providers/google-login-provider'

export interface SocialAuthServiceConfig {
    autoLogin?: boolean
    providers: { id: string; provider: LoginProvider }[]
    onError?: (error: any) => any
}

/** @dynamic */
@Injectable()
export class SocialAuthService {
    private static readonly ERR_LOGIN_PROVIDER_NOT_FOUND =
        'Login provider not found'
    private static readonly ERR_NOT_LOGGED_IN = 'Not logged in'
    private static readonly ERR_NOT_INITIALIZED =
        'Login providers not ready yet. Are there errors on your console?'
    private static readonly ERR_NOT_SUPPORTED_FOR_REFRESH_TOKEN =
        'Chosen login provider is not supported for refreshing a token'

    private providers: Map<string, LoginProvider> = new Map()
    private providerInitPromises: Map<string, Promise<void>> = new Map()
    private autoLogin = false

    private _user: SocialUser = null
    private _authState: ReplaySubject<SocialUser> = new ReplaySubject(1)

    /* Consider making this an enum comprising LOADING, LOADED, FAILED etc. */
    private initialized = false
    private _initState: AsyncSubject<boolean> = new AsyncSubject()

    get authState(): Observable<SocialUser> {
        return this._authState.asObservable()
    }

    get initState(): Observable<boolean> {
        return this._initState.asObservable()
    }

    constructor() {
        const config = inject<
            SocialAuthServiceConfig | Promise<SocialAuthServiceConfig>
        >('SocialAuthServiceConfig' as any)

        if (config instanceof Promise) {
            config.then((config) => {
                this.initialize(config)
            })
        } else {
            this.initialize(config)
        }
    }

    private initialize(config: SocialAuthServiceConfig) {
        this.autoLogin =
            config.autoLogin !== undefined ? config.autoLogin : false
        const { onError = console.error } = config

        config.providers.forEach((item) => {
            this.providers.set(item.id, item.provider)
        })

        const providerIds = Array.from(this.providers.keys())
        const initializePromise = this.autoLogin
            ? Promise.all(
                  providerIds.map((providerId) =>
                      this.initializeProvider(providerId),
                  ),
              )
            : Promise.resolve()

        initializePromise
            .then(() => {
                if (this.autoLogin) {
                    const loginStatusPromises = []
                    let loggedIn = false

                    this.providers.forEach(
                        (provider: LoginProvider, key: string) => {
                            const promise = provider.getLoginStatus()
                            loginStatusPromises.push(promise)
                            promise
                                .then((user: SocialUser) => {
                                    user.provider = key

                                    this._user = user
                                    this._authState.next(user)
                                    loggedIn = true
                                })
                                .catch(console.debug)
                        },
                    )
                    Promise.all(loginStatusPromises).catch(() => {
                        if (!loggedIn) {
                            this._user = null
                            this._authState.next(null)
                        }
                    })
                }
            })
            .catch((error) => {
                onError(error)
            })
            .finally(() => {
                this.initialized = true
                this._initState.next(this.initialized)
                this._initState.complete()
            })
    }

    private initializeProvider(providerId: string): Promise<void> {
        const existingPromise = this.providerInitPromises.get(providerId)
        if (existingPromise) {
            return existingPromise
        }

        const providerObject = this.providers.get(providerId)
        if (!providerObject) {
            return Promise.reject(
                SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND,
            )
        }

        const initPromise = providerObject.initialize().catch((error) => {
            this.providerInitPromises.delete(providerId)
            throw error
        })
        this.providerInitPromises.set(providerId, initPromise)
        return initPromise
    }

    refreshAuthToken(providerId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.initialized) {
                reject(SocialAuthService.ERR_NOT_INITIALIZED)
            } else if (providerId !== GoogleLoginProvider.PROVIDER_ID) {
                reject(SocialAuthService.ERR_NOT_SUPPORTED_FOR_REFRESH_TOKEN)
            } else {
                const providerObject = this.providers.get(providerId)
                if (providerObject) {
                    this.initializeProvider(providerId)
                        .then(() =>
                            providerObject.getLoginStatus({
                                refreshToken: true,
                            }),
                        )
                        .then((user: SocialUser) => {
                            user.provider = providerId
                            this._user = user
                            this._authState.next(user)
                            resolve()
                        })
                        .catch((err) => {
                            reject(err)
                        })
                } else {
                    reject(SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND)
                }
            }
        })
    }

    signIn(providerId: string, signInOptions?: any): Promise<SocialUser> {
        return new Promise((resolve, reject) => {
            if (!this.initialized) {
                reject(SocialAuthService.ERR_NOT_INITIALIZED)
            } else {
                const providerObject = this.providers.get(providerId)
                if (providerObject) {
                    this.initializeProvider(providerId)
                        .then(() => providerObject.signIn(signInOptions))
                        .then((user: SocialUser) => {
                            user.provider = providerId
                            resolve(user)

                            this._user = user
                            this._authState.next(user)
                        })
                        .catch((err) => {
                            reject(err)
                        })
                } else {
                    reject(SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND)
                }
            }
        })
    }

    signOut(revoke: boolean = false): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.initialized) {
                reject(SocialAuthService.ERR_NOT_INITIALIZED)
            } else if (!this._user) {
                reject(SocialAuthService.ERR_NOT_LOGGED_IN)
            } else {
                const providerId = this._user.provider
                const providerObject = this.providers.get(providerId)
                if (providerObject) {
                    providerObject
                        .signOut(revoke)
                        .then(() => {
                            resolve(null)

                            this._user = null
                            this._authState.next(null)
                        })
                        .catch((err) => {
                            reject(err)
                        })
                } else {
                    reject(SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND)
                }
            }
        })
    }
}
